import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscardBar, Modal, type ModalSize } from './Modal.tsx';
import { Button, ErrorBox, Field, Textarea } from './ui.tsx';

/**
 * Formulário de cadastro ou edição em janela, no padrão dos Dados Mestres (o mesmo da ficha da
 * Empresa): campos compactos, legenda dos obrigatórios e Cancelar / Salvar fixos no rodapé, erro
 * do servidor no topo e o de cada campo ao lado dele (`issueFor`). Cancelar, X, Esc ou clicar fora
 * com algo alterado pedem confirmação antes de descartar. Só consulta: só o botão Fechar.
 */
export function FormModal(props: FormModalProps) {
  // A janela só existe aberta: ao fechar, a confirmação de descarte volta ao estado inicial.
  if (!props.open) return null;
  return <FormModalOpen {...props} />;
}

type FormModalProps = {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** `lg` (padrão): duas colunas; `md`: uma coluna; `xl`: muitos campos. */
  size?: ModalSize;
  /** Há algo alterado e não salvo. */
  dirty: boolean;
  saving?: boolean;
  /** Erro da última tentativa de salvar. */
  error?: unknown;
  /** Só consulta: campos bloqueados por quem chama e o rodapé só com Fechar. */
  readOnly?: boolean;
  /** Texto do botão principal ("Criar unidade", "Salvar"). Padrão: Salvar. */
  saveLabel?: string;
  /** `danger` para ações que bloqueiam ou recusam (Bloquear, Rejeitar). */
  saveTone?: 'primary' | 'danger';
  /** Sem campo obrigatório na janela: `false` tira a legenda "Campos com * são obrigatórios.". */
  requiredLegend?: boolean;
  /** Texto do botão de desistir. Padrão: Cancelar; use Voltar quando a ação já se chama Cancelar… */
  cancelLabel?: string;
  onSave: () => void;
  onClose: () => void;
  /** Cadastro longo em blocos: faixa de abas fixa abaixo do título (use com `size="record"`). */
  tabs?: ReactNode;
  children: ReactNode;
};

function FormModalOpen({
  title,
  description,
  size = 'lg',
  dirty,
  saving = false,
  error,
  readOnly = false,
  saveLabel,
  saveTone = 'primary',
  requiredLegend = true,
  cancelLabel,
  onSave,
  onClose,
  tabs,
  children,
}: FormModalProps) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const cancel = () => (dirty ? setConfirming(true) : onClose());
  return (
    <Modal
      open
      size={size}
      compact
      title={title}
      description={description}
      dirty={dirty && !readOnly}
      onClose={onClose}
      onSubmit={readOnly ? undefined : onSave}
      tabs={tabs}
      footerBar={
        confirming ? (
          <DiscardBar onKeep={() => setConfirming(false)} onDiscard={onClose} />
        ) : undefined
      }
      footer={
        readOnly ? (
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <span className="mr-auto basis-full self-center text-xs text-muted sm:basis-auto">
              {requiredLegend && t('common.requiredLegend')}
            </span>
            <Button variant="secondary" onClick={cancel}>
              {cancelLabel ?? t('common.cancel')}
            </Button>
            <Button type="submit" variant={saveTone} disabled={saving}>
              {saving ? t('common.saving') : (saveLabel ?? t('common.save'))}
            </Button>
          </>
        )
      }
    >
      <ErrorBox error={error} showPaths={false} />
      {children}
    </Modal>
  );
}

/** Bloco de campos com título discreto (Identificação, Responsáveis…), em grade de 1 a 4 colunas. */
export function FormSection({
  title,
  columns = 2,
  children,
}: {
  title?: string;
  columns?: 1 | 2 | 3 | 4;
  children: ReactNode;
}) {
  const grid = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[columns];
  return (
    <section className="flex flex-col gap-3">
      {title && (
        <h3 className="border-b border-rule pb-1.5 text-xs font-semibold text-muted">{title}</h3>
      )}
      <div className={`grid gap-x-4 gap-y-3 ${grid}`}>{children}</div>
    </section>
  );
}

/**
 * Ação com motivo (variação 6 do guia): uma frase com a consequência, o motivo obrigatório e o
 * botão no tom da ação. Quem chama fecha a janela quando a ação termina sem erro.
 */
export function ReasonDialog(props: ReasonDialogProps) {
  // A janela só existe aberta: ao reabrir, o motivo começa vazio.
  if (!props.open) return null;
  return <ReasonDialogOpen {...props} />;
}

type ReasonDialogProps = {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  fieldLabel: string;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  /** Texto do botão de desistir (Voltar, quando a ação já se chama Cancelar…). Padrão: Cancelar. */
  cancelLabel?: string;
  pending?: boolean;
  /** Erro do envio feito nesta janela. */
  error?: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
};

function ReasonDialogOpen({
  title,
  description,
  fieldLabel,
  confirmLabel,
  tone = 'primary',
  cancelLabel,
  pending = false,
  error,
  onConfirm,
  onClose,
}: ReasonDialogProps) {
  const [reason, setReason] = useState('');
  return (
    <FormModal
      open
      size="md"
      title={title}
      description={description}
      dirty={reason.trim() !== ''}
      saving={pending}
      error={error}
      saveLabel={confirmLabel}
      saveTone={tone}
      cancelLabel={cancelLabel}
      onSave={() => reason.trim() !== '' && onConfirm(reason.trim())}
      onClose={onClose}
    >
      <FormSection columns={1}>
        <Field label={fieldLabel} required>
          <Textarea required value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Algo mudou em relação ao valor inicial do formulário? */
export function changed<T>(initial: T, current: T): boolean {
  return JSON.stringify(initial) !== JSON.stringify(current);
}
